import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { API_URL, apiFetch } from '@/config/api';

import {
  SAVED_EVAL_POLL_MS,
  stillWaitingForEvalResults,
  type PendingEvalWait,
} from './eval-result-view';

import type {
  AgentConfigNameList,
  AgentConfigPreview,
  AgentEvalOptions,
  SavedAgentEvalQuery,
  SavedAgentEvalResponse,
  StartAgentEvalRequest,
  StartAgentEvalResponse,
} from './agent-config.types';

const STALE_TIME_MS = 5 * 60 * 1000;

async function fetchAdminJson<T>(token: string, path: string): Promise<T> {
  const response = await apiFetch(`${API_URL}${path}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(formatAdminError(error.detail, 'Request failed'));
  }
  return response.json() as Promise<T>;
}

function formatAdminError(detail: unknown, fallback: string): string {
  if (typeof detail === 'string' && detail) return detail;
  if (detail && typeof detail === 'object' && 'user_error_message' in detail) {
    const message = (detail as { user_error_message?: string })
      .user_error_message;
    if (message) return message;
  }
  return fallback;
}

function formatNetworkError(error: unknown, fallback: string): string {
  if (
    error instanceof TypeError &&
    /load failed|failed to fetch|networkerror/i.test(error.message)
  ) {
    return 'Could not reach the API. Is the backend running?';
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function useAgentConfigNames() {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['admin', 'agent-configs'],
    queryFn: async (): Promise<AgentConfigNameList> =>
      fetchAdminJson(await getAccessTokenSilently(), '/admin/agent-configs'),
    staleTime: STALE_TIME_MS,
  });
}

export function useAgentConfigPreview(agentName: string | undefined) {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['admin', 'agent-configs', agentName],
    queryFn: async (): Promise<AgentConfigPreview> =>
      fetchAdminJson(
        await getAccessTokenSilently(),
        `/admin/agent-configs/${encodeURIComponent(agentName ?? '')}`
      ),
    enabled: Boolean(agentName),
    staleTime: STALE_TIME_MS,
  });
}

export function useAgentEvalOptions() {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['admin', 'agent-configs', 'eval-options'],
    queryFn: async (): Promise<AgentEvalOptions> =>
      fetchAdminJson(
        await getAccessTokenSilently(),
        '/admin/agent-configs/eval-options'
      ),
    staleTime: STALE_TIME_MS,
  });
}

export function useSavedAgentEvals(
  query: SavedAgentEvalQuery | null,
  pending: PendingEvalWait | null = null
) {
  const { getAccessTokenSilently } = useAuth0();
  const params = new URLSearchParams();
  if (query) {
    params.set('metric', query.metric);
    params.set('max_questions', String(query.max_questions));
    for (const agent of query.agents) {
      params.append('agents', agent);
    }
  }
  return useQuery({
    queryKey: ['admin', 'agent-configs', 'evals', 'saved', query],
    queryFn: async (): Promise<SavedAgentEvalResponse> =>
      fetchAdminJson(
        await getAccessTokenSilently(),
        `/admin/agent-configs/evals/saved?${params.toString()}`
      ),
    enabled: Boolean(query),
    staleTime: 30 * 1000,
    refetchInterval: (savedQuery) =>
      stillWaitingForEvalResults(pending, savedQuery.state.data?.runs)
        ? SAVED_EVAL_POLL_MS
        : false,
  });
}

export function useStartAgentEval() {
  const { getAccessTokenSilently } = useAuth0();

  return useMutation({
    mutationFn: async (
      payload: StartAgentEvalRequest
    ): Promise<StartAgentEvalResponse> => {
      const token = await getAccessTokenSilently();
      let response: Response;
      try {
        response = await apiFetch(`${API_URL}/admin/agent-configs/evals`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
      } catch (error) {
        throw new Error(formatNetworkError(error, 'Failed to start eval'));
      }
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(formatAdminError(error.detail, 'Failed to start eval'));
      }
      return response.json() as Promise<StartAgentEvalResponse>;
    },
  });
}
